import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { BarChart3, PieChart as PieChartIcon, List } from 'lucide-react';
import type { LeadConversion, LeadConversionPlatform } from '../../../types';

// Brand-ish accents per platform, falling back to slate for a source the map
// does not know — the backend passes an unrecognised platform through verbatim
// rather than bucketing it, so new campaign sources appear here as themselves.
const PLATFORM_STYLES: Record<string, { bar: string; dot: string; text: string }> = {
  Instagram: { bar: 'bg-pink-500', dot: 'bg-pink-500', text: 'text-pink-600' },
  Facebook: { bar: 'bg-blue-600', dot: 'bg-blue-600', text: 'text-blue-600' },
  WhatsApp: { bar: 'bg-emerald-500', dot: 'bg-emerald-500', text: 'text-emerald-600' },
  Call: { bar: 'bg-amber-500', dot: 'bg-amber-500', text: 'text-amber-600' },
  Reference: { bar: 'bg-indigo-500', dot: 'bg-indigo-500', text: 'text-indigo-600' },
};

// Same accents as PLATFORM_STYLES, as hex — recharts fills/strokes take colors,
// not Tailwind classes.
const PLATFORM_HEX: Record<string, string> = {
  Instagram: '#ec4899',
  Facebook: '#2563eb',
  WhatsApp: '#10b981',
  Call: '#f59e0b',
  Reference: '#6366f1',
};

const hexFor = (platform: string) => PLATFORM_HEX[platform] || '#94a3b8';

const styleFor = (platform: string) =>
  PLATFORM_STYLES[platform] || { bar: 'bg-slate-400', dot: 'bg-slate-400', text: 'text-slate-500' };

const FunnelTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
  if (active && payload && payload.length) {
    const headerLabel = label ?? payload[0]?.payload?.name ?? payload[0]?.name;
    return (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-200">
        <p className="font-bold text-xs text-slate-500 uppercase tracking-wider mb-2 border-b border-slate-100 pb-1.5">{headerLabel}</p>
        <div className="space-y-1.5">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <span className="text-[11px] font-medium text-slate-500">{entry.dataKey ?? entry.name}:</span>
              <span className="text-[12px] font-bold" style={{ color: entry.color ?? entry.payload?.fill }}>{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v}%`;

interface Props {
  data: LeadConversion | null;
  /** The window the figures cover, e.g. "2026-09" — rendered as-is. */
  period?: string;
  loading?: boolean;
  error?: string | null;
  /** Extra classes for the outer card, so each screen can set its own spacing. */
  className?: string;
}

const Stage: React.FC<{ label: string; value: number; tone?: string }> = ({
  label, value, tone = 'text-slate-800',
}) => (
  <div className="min-w-[64px]">
    <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{label}</div>
    <div className={`text-[15px] font-black ${tone}`}>{value}</div>
  </div>
);

/**
 * Where the month's leads came from, and how far each platform got.
 *
 * The cohort is leads UPLOADED in the window, so `conversion_pct` compares like
 * with like: a lead uploaded in August that pays in September belongs to
 * August's funnel. `paid` counts LEADS (a lead who bought twice is one
 * conversion); `paid_orders` is the order count read off the orders list, shown
 * beside it only when the two differ — otherwise it is noise.
 */
const LeadConversionCard: React.FC<Props> = ({
  data, period, loading, error, className = '',
}) => {
  // "default" is the list-only view (no chart below it) — the card opens here
  // and Bar/Pie are opt-in, not the other way round.
  const [chartType, setChartType] = useState<'default' | 'bar' | 'pie'>('default');
  // "Marketing Orders" is an internal order-entry channel, not a lead source —
  // it has no place in a source-attribution breakdown, so it's dropped here
  // rather than at the API layer.
  const platforms: LeadConversionPlatform[] = (data?.platforms || []).filter(
    (p) => p.platform !== 'Marketing Orders',
  );
  // Recomputed from the filtered list — using the API's own totals here would
  // silently re-include the dropped source's numbers.
  const totals = data?.platforms
    ? {
        uploaded: platforms.reduce((sum, p) => sum + p.uploaded, 0),
        ordered: platforms.reduce((sum, p) => sum + p.ordered, 0),
        paid: platforms.reduce((sum, p) => sum + p.paid, 0),
        paid_orders: platforms.reduce((sum, p) => sum + p.paid_orders, 0),
        conversion_pct: null as number | null,
      }
    : undefined;
  if (totals) {
    totals.conversion_pct = totals.uploaded > 0
      ? Math.round((totals.paid / totals.uploaded) * 1000) / 10
      : null;
  }
  // Bars are scaled against the busiest platform, not against the total, so a
  // two-platform month still fills the row instead of showing two half bars.
  const peak = Math.max(1, ...platforms.map((p) => p.uploaded));

  const funnelChartData = platforms.map((p) => ({
    name: p.platform,
    Uploaded: p.uploaded,
    Ordered: p.ordered,
    Paid: p.paid,
  }));
  const paidShareData = platforms.map((p) => ({
    name: p.platform,
    value: p.paid,
  }));

  return (
    <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6 space-y-4 ${className}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-sm font-black text-slate-700 uppercase tracking-wider">
          Lead conversion by source
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-400">
            leads uploaded{period ? ` in ${period}` : ''}, and how far each source got
          </span>
          <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
            <button
              type="button"
              onClick={() => setChartType('default')}
              title="Default (no chart)"
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer outline-none focus:outline-none ${chartType === 'default' ? 'bg-white shadow-xs text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <List size={13} />
            </button>
            <button
              type="button"
              onClick={() => setChartType('bar')}
              title="Bar chart"
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer outline-none focus:outline-none ${chartType === 'bar' ? 'bg-white shadow-xs text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <BarChart3 size={13} />
            </button>
            <button
              type="button"
              onClick={() => setChartType('pie')}
              title="Pie chart"
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer outline-none focus:outline-none ${chartType === 'pie' ? 'bg-white shadow-xs text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <PieChartIcon size={13} />
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="py-8 text-center text-rose-500 font-medium text-[13px]">{error}</div>
      )}

      {/* Every known source is on the board even at zero, so an empty month
          still shows its rows — "WhatsApp brought in nothing" and "the WhatsApp
          row is missing" must not look the same. This note says which it is. */}
      {!loading && !error && platforms.length > 0 && totals?.uploaded === 0 && (
        <div className="text-[12px] text-slate-400 font-medium">
          No leads were uploaded in this period.
        </div>
      )}

      {!loading && !error && platforms.length === 0 && (
        <div className="py-8 text-center text-slate-400 font-medium text-[13px]">
          No lead sources to show.
        </div>
      )}

      {!loading && !error && platforms.length > 0 && chartType === 'default' && (
        <>
          <div className="space-y-3">
            {platforms.map((p) => {
              const style = styleFor(p.platform);
              return (
                <div key={p.platform} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                      <span className="text-[13px] font-bold text-slate-800">{p.platform}</span>
                    </div>

                    <div className="flex items-center flex-wrap gap-x-4 gap-y-2 sm:gap-x-7">
                      <Stage label="Uploaded" value={p.uploaded} />
                      <Stage label="Ordered" value={p.ordered} tone="text-[#465FFF]" />
                      <Stage label="Paid" value={p.paid} tone="text-emerald-700" />
                      <div className="min-w-[64px]">
                        <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                          Conversion
                        </div>
                        <div className={`text-[15px] font-black ${style.text}`}>
                          {pct(p.conversion_pct)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Uploaded is the track; paid is the fill. Ordered sits between
                      them as a lighter marker, so the drop-off is visible. */}
                  <div className="relative h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-slate-200"
                      style={{ width: `${(p.uploaded / peak) * 100}%` }}
                    />
                    <div
                      className={`absolute inset-y-0 left-0 ${style.bar} opacity-40`}
                      style={{ width: `${(p.ordered / peak) * 100}%` }}
                    />
                    <div
                      className={`absolute inset-y-0 left-0 ${style.bar}`}
                      style={{ width: `${(p.paid / peak) * 100}%` }}
                    />
                  </div>

                  {/* Only when a lead bought more than once — otherwise the two
                      figures are identical and printing both invites a re-read. */}
                  {p.paid_orders !== p.paid && (
                    <div className="text-[10px] text-slate-400 font-medium">
                      {p.paid_orders} paid orders from {p.paid} converted lead
                      {p.paid === 1 ? '' : 's'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {totals && (
            <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                All sources
              </span>
              <div className="flex items-center flex-wrap gap-x-4 gap-y-2 sm:gap-x-7">
                <Stage label="Uploaded" value={totals.uploaded} />
                <Stage label="Ordered" value={totals.ordered} tone="text-[#465FFF]" />
                <Stage label="Paid" value={totals.paid} tone="text-emerald-700" />
                <div className="min-w-[64px]">
                  <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    Conversion
                  </div>
                  <div className="text-[15px] font-black text-slate-800">
                    {pct(totals.conversion_pct)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!loading && !error && platforms.length > 0 && chartType !== 'default' && (
          <div className="pt-3 border-t border-slate-100">
            {chartType === 'pie' ? (
              <div>
                <div className="h-[240px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart accessibilityLayer={false}>
                      <Tooltip content={<FunnelTooltip />} />
                      <Pie
                        data={paidShareData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={70}
                        paddingAngle={2}
                        animationDuration={1000}
                        animationEasing="ease-out"
                        label={({ value }) => (value > 0 ? value : '')}
                        labelLine={false}
                      >
                        {paidShareData.map((entry, index) => (
                          <Cell key={`platform-cell-${index}`} fill={hexFor(entry.name)} stroke="#fff" strokeWidth={2} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-[10px] text-slate-400 text-center font-medium">Share of paid conversions by source</p>
                {/* Color key — the tooltip only shows a source's name/value on
                    hover, so without this the slice colors mean nothing at rest.
                    Sibling of the fixed-height chart box above (not nested
                    inside it) so it takes its own space instead of overflowing
                    past a box the chart already fills completely. */}
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-1.5">
                  {paidShareData.map((entry) => (
                    <span key={entry.name} className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: hexFor(entry.name) }} />
                      {entry.name}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[240px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnelChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} accessibilityLayer={false}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} width={35} allowDecimals={false} />
                    <Tooltip content={<FunnelTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Bar dataKey="Uploaded" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={22} animationDuration={1000} animationEasing="ease-out" />
                    <Bar dataKey="Ordered" fill="#465FFF" radius={[4, 4, 0, 0]} maxBarSize={22} animationDuration={1000} animationEasing="ease-out" />
                    <Bar dataKey="Paid" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={22} animationDuration={1000} animationEasing="ease-out" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {chartType !== 'pie' && (
              <div className="flex items-center justify-center gap-4 text-[11px] font-semibold text-slate-500 mt-1">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" />Uploaded</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: '#465FFF' }} />Ordered</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Paid</span>
              </div>
            )}
          </div>
      )}
    </div>
  );
};

export default LeadConversionCard;
